import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Hammer, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { gbp } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { quoteFilePath, SLOT_LABEL } from "../../lib/subby";
import { Card, EmptyState, Modal, PrimaryButton, QuietButton } from "../../components/UI";
import { PdfViewerModal } from "../../components/PdfViewerModal";
import { SubbyQuoteDialog } from "../../components/subby/SubbyQuoteDialog";
import { SubbyQuoteFileDialog } from "../../components/subby/SubbyQuoteFileDialog";
import { SubbyQuoteCard } from "../../components/subby/SubbyQuoteCard";
import { SubbyQuoteDetailsModal } from "../../components/subby/SubbyQuoteDetailsModal";

function VoidDialog({ open, quote, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setReason(""); setBusy(false); } }, [open]);
  if (!quote) return null;
  return (
    <Modal open={open} onClose={onClose} title="Void this quote?" testid="subby-void-dialog"
      footer={<>
        <QuietButton onClick={onClose} data-testid="subby-void-cancel">Cancel</QuietButton>
        <PrimaryButton disabled={!reason.trim() || busy} onClick={async () => { setBusy(true); await onConfirm(reason.trim()); setBusy(false); }} data-testid="subby-void-confirm">Void quote</PrimaryButton>
      </>}>
      <p className="text-ink-2 mb-3" style={{ fontSize: 12.5 }}>
        {quote.subcontractor_name} · {gbp(quote.amount)}. The row stays on the tender marked VOID and every stored file remains downloadable. Upload the corrected quote as a new record.
        {quote.accepted && <span className="block mt-1" style={{ color: "#7A4C0A" }}>This is the accepted quote for {quote.trade_name || "its trade"} — voiding it clears that acceptance.</span>}
      </p>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="subby-void-reason"
        className="w-full h-9 px-3 rounded-md border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent" style={{ fontSize: 13 }} />
    </Modal>
  );
}

/** LT-37 — tender Subby Quotes tab. LT-39 — accept a quote per trade, details / notes modal, in-app PDF viewer. */
export function SubbyQuotesTab({ tender }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVoid, setShowVoid] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [fileTarget, setFileTarget] = useState(null);   // {quote, slot}
  const [voidTarget, setVoidTarget] = useState(null);
  const [detailsId, setDetailsId] = useState(null);
  const [viewer, setViewer] = useState(null);           // {quote, slot, rev}

  const load = () => {
    api.get(`/tenders/${tender.id}/subby-quotes`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e, "Failed to load subby quotes")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [tender.id]); // eslint-disable-line

  const replaceQuote = (q) => setData((d) => (d ? { ...d, quotes: d.quotes.map((x) => (x.id === q.id ? { ...x, ...q } : x)) } : d));

  const doVoid = async (reason) => {
    try {
      await api.post(`/tenders/${tender.id}/subby-quotes/${voidTarget.id}/void`, { reason });
      toast.success("Quote voided");
      setVoidTarget(null); load();
    } catch (e) { toast.error(formatApiError(e, "Could not void the quote")); }
  };
  const doAccept = async (q) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/subby-quotes/${q.id}/accept`);
      const n = (r.data.superseded_quote_ids || []).length;
      toast.success(n ? `${q.subcontractor_name} accepted — the previous ${q.trade_name || "trade"} winner stepped down` : `${q.subcontractor_name} accepted for ${q.trade_name || "this work"}`);
      load();
    } catch (e) { toast.error(formatApiError(e, "Could not accept the quote")); }
  };
  const doUnaccept = async (q) => {
    try {
      await api.post(`/tenders/${tender.id}/subby-quotes/${q.id}/unaccept`);
      toast.success("Acceptance removed");
      load();
    } catch (e) { toast.error(formatApiError(e, "Could not un-accept the quote")); }
  };

  const all = data?.quotes || [];
  const quotes = all.filter((q) => showVoid || q.status === "active");
  const voidCount = all.filter((q) => q.status === "void").length;
  const details = all.find((q) => q.id === detailsId) || null;

  return (
    <div className="mt-6" data-testid="subby-quotes-tab">
      <div className="flex items-center justify-between gap-4 mb-4">
        <div>
          <h3 className="font-display text-ink" style={{ fontSize: 15, fontWeight: 600 }}>Subby quotes</h3>
          <div className="text-ink-3 mt-0.5" style={{ fontSize: 12 }} data-testid="subby-quotes-summary">
            {data ? <>{data.active_count} active · <span className="font-mono tnum">{gbp(data.active_total)}</span> quoted
              {data.accepted_count > 0 && <> · <span className="text-accent" data-testid="subby-quotes-accepted-summary">{data.accepted_count} accepted · <span className="font-mono tnum">{gbp(data.accepted_total)}</span></span></>}</> : "…"}
            {data?.tender_issued_at && <> · client holds {data.tender_issued_version || "an issued version"}</>}
            {" · "}<Link to="/labour-rates?tab=directory" className="underline hover:text-ink" data-testid="subby-quotes-directory-link">Directory</Link>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {voidCount > 0 && (
            <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 12 }}>
              <input type="checkbox" checked={showVoid} onChange={(e) => setShowVoid(e.target.checked)} data-testid="subby-quotes-show-void" /> Show void ({voidCount})
            </label>
          )}
          <Link to="?tab=email" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-line text-ink-2 hover:bg-surface-2" style={{ fontSize: 12 }} data-testid="subby-quotes-email-link">
            <Mail size={12} /> Send email
          </Link>
          <PrimaryButton icon={Plus} onClick={() => setAddOpen(true)} data-testid="subby-quotes-add">Add quote</PrimaryButton>
        </div>
      </div>

      {loading ? (
        <div className="text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
      ) : quotes.length === 0 ? (
        <Card>
          <div data-testid="subby-quotes-empty">
            <EmptyState icon={Hammer} title="No subby quotes yet"
              hint="Upload the quote as received (original) and, when ready, the client-facing copy. Files are kept as revisions and never replaced."
              action={<PrimaryButton icon={Plus} onClick={() => setAddOpen(true)}>Add quote</PrimaryButton>} />
          </div>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="subby-quotes-list">
          {quotes.map((q) => (
            <SubbyQuoteCard key={q.id} tenderId={tender.id} quote={q}
              onAddRevision={(quote, slot) => setFileTarget({ quote, slot })}
              onVoid={setVoidTarget} onChanged={load}
              onView={(quote, slot, rev) => setViewer({ quote, slot, rev })}
              onDetails={(quote) => setDetailsId(quote.id)}
              onAccept={doAccept} onUnaccept={doUnaccept} />
          ))}
        </div>
      )}

      <SubbyQuoteDialog open={addOpen} onClose={() => setAddOpen(false)} tenderId={tender.id} onSaved={load} />
      <SubbyQuoteFileDialog open={!!fileTarget} onClose={() => setFileTarget(null)} tenderId={tender.id}
        quote={fileTarget?.quote} slot={fileTarget?.slot} onSaved={load} />
      <VoidDialog open={!!voidTarget} quote={voidTarget} onClose={() => setVoidTarget(null)} onConfirm={doVoid} />
      <SubbyQuoteDetailsModal open={!!details} onClose={() => setDetailsId(null)} tenderId={tender.id} quote={details}
        onChanged={replaceQuote} onView={(slot, rev) => setViewer({ quote: details, slot, rev })}
        onAccept={doAccept} onUnaccept={doUnaccept} />
      <PdfViewerModal open={!!viewer} onClose={() => setViewer(null)}
        src={viewer ? quoteFilePath(tender.id, viewer.quote.id, viewer.slot, viewer.rev.rev) : null}
        title={viewer ? `${viewer.quote.subcontractor_name} — ${SLOT_LABEL[viewer.slot]} r${viewer.rev.rev}` : ""}
        subtitle={viewer ? `${gbp(viewer.quote.amount)} · ${viewer.quote.trade_name || "no trade"}` : ""}
        filename={viewer?.rev?.filename} testid="subby-pdf-viewer" />
    </div>
  );
}
